



































































































































/* General */
import Vue from 'vue'
import { isEqual, uniq } from 'lodash'
import DefaultLayout from '@/layouts/default-layout.vue'
import MainContainer from '@/layouts/main-container.vue'
import EntityChildLayout from '@/layouts/entity-child-layout.vue'

/* Mixins */

/* Components */
import TaskDetailTopbar from './components/task-detail-topbar.vue'
import TaskDetailHeader from './components/task-detail-header.vue'
import TaskDetailChronology from './components/task-detail-chronology.vue'

/* Helpers */
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import LocalStore from '@/helpers/localstore'

/* Data types */
import {
  IChronologyItemTypes,
  IChronologyItem,
} from '@/types/data-objects/item-histories'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IAtMentionUser,
  IPriorityLevelForSelection,
  IStatusForSelection,
  IDescriptionTemplate,
  ILinkedItemForListing,
  IPersonWithEmail,
  ITasksOutputHistory,
  IItemResponder,
  IJobAppointee,
  IJobAssigneeMandate,
  IChecklist,
} from '@/api/types/response.types'
import {
  ITasksInputEditComment,
  ITasksInputUpdateJobAppointee,
} from '@/api/types/request.types'
import { IProfile } from '@/types/data-objects/profile'
import {
  IBreadcrumbItemMenuItem,
  ICommentOrReplyData,
  IJobInfo,
} from '@/types/data-objects/items'
import { EEntities } from '@/types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import DashboardAPI from '@/api/modules/dashboard.api'

export default Vue.extend({
  name: 'TaskDetail',

  components: {
    DefaultLayout,
    MainContainer,
    EntityChildLayout,
    TaskDetailTopbar,
    TaskDetailHeader,
    TaskDetailChronology,
  },

  metaInfo(): any {
    if (this.refNumber !== null && this.formData.title) {
      return {
        title: `#${this.refNumber}: ${this.formData.title}`,
      }
    } else {
      return {
        title: '',
      }
    }
  },

  data() {
    return {
      EEntities,

      // Details
      refNumber: 0,
      projectName: '',
      creationDate: '',
      showHarvestOption: false,
      tags: [] as string[],
      globalAttachments: [],
      tagsOptions: [] as string[],
      atMentionUsers: [] as IAtMentionUser[],
      respondersSinceLastView: [] as IItemResponder[],
      isPinned: false,
      jobAppointees: [] as IJobAppointee[],

      // For job info...
      currentAssignee: null,
      currentStatus: null,

      // Options
      statusOptions: [] as IStatusForSelection[],
      priorityOptions: [] as IPriorityLevelForSelection[],
      assigneesOptions: [] as IPersonWithEmail[],
      watchersOptions: [] as IPersonWithEmail[],
      hasGuestUsers: false,

      // UI controls
      isDataLoading: true,
      isSubmitting: false,
      isPostSubmitPreRefetchData: false,
      sendBackButtonToRoot: false,
      addCommentEditorOpened: false,
      showHarvestModal: false,
      isTrafficCopChecking: false,
      showMoveTaskModal: false,

      fromTaskCreate: false,
      formData: {
        dueDate: null,
        assigneeID: 0,
        priorityID: 0,
        statusID: 0,
        title: '',
        description: '',
        listTags: [],
        listWatchers: [],
        initialAttachments: [],
        creator: {} as IPersonWithEmail,
      },
      history: [] as ITasksOutputHistory[],
      linkedTasks: [] as ILinkedItemForListing[],
      linkedConversations: [] as ILinkedItemForListing[],
      checklists: [] as IChecklist[],
      descriptionTemplates: [] as IDescriptionTemplate[],
      assigneeMandates: [] as IJobAssigneeMandate[],

      /* Polling data */
      showStaleAlert: false,
      lastAccessEpochTime: null,
      poller: null,
      pollIntervalInMS: this.$store.get('app/autoRefreshDataPingInMS'),
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    profile(): IProfile {
      return this.$permissions.loggedInUser
    },
    permaLink(): string {
      return window.location.href
    },
    projectID(): number {
      return Number(this.$route.params.id)
    },
    taskID(): number {
      return Number(this.$route.params.childId)
    },
    historyWithTypes(): IChronologyItem[] {
      const result = this.history.slice(1).map((item: ITasksOutputHistory) => {
        if (item.description || item.attachments.length > 0) {
          return {
            data: {
              ...item,
              mentionUsers: this.atMentionUsers,
            },
            type:
              item.taskEventTypeID === 8
                ? IChronologyItemTypes.INTERNAL_COMMENT
                : IChronologyItemTypes.COMMENT,
          }
        } else {
          return {
            data: { ...item },
            type: IChronologyItemTypes.ACTION,
          }
        }
      })

      return result
    },
    jobInfo(): IJobInfo {
      if (this.jobAppointees.length === 0) {
        return null
      } else {
        return {
          jobAppointees: this.jobAppointees,
          assigneeMandates: this.assigneeMandates,
          currentAssignee: this.currentAssignee,
          currentStatus: this.currentStatus,
        }
      }
    },
  },

  beforeDestroy() {
    this.endPolling()
  },

  async created() {
    this.isDataLoading = true
    try {
      await this.fetchData()
    } catch (error) {
      this.$error.emit(error)
    } finally {
      this.isDataLoading = false
    }
    this.startPolling()
  },

  mounted() {
    // Pre-seed ref number from params, if available, so there's no
    // glitch when pulling it in from API...

    // Pre-seed data from the route.params, if available (coming from internal routing)
    // This will help breadcrumb-for-item-detail feel "smooth" page-to-page since doesn't have to wait
    // for data load. Data load is still good in case project name changed in interim, for example.
    if (this.$route.params.refNumber) {
      this.refNumber = parseInt(this.$route.params.refNumber, 10)
    }
    // Specifically used for "from_all" or going from linked tasks. The "from_entity_landing" scenario
    // will have data passed from VueX cache.
    if (this.$route.params.projectName) {
      this.projectName = this.$route.params.projectName
    }

    // NOTE: LocalStore.itemDetailBackBtnToRoot only set to true when navigating the prev/next buttons
    // or coming in here from being freshly created. So, the very first landing of a detail from the
    // listing pages will not have this be true and it will simply lean on window.history.go(-1). Subsequent
    // ones will be true and then go back to the listing where item filter settings are fetched
    // from localstore (and btw, we hard set the page back to 1 always. Maybe this should be left so
    // they stay on the page they had come from...but should move pagination to the top of listing for clarity)
    if (LocalStore.itemDetailBackBtnToRoot) {
      this.sendBackButtonToRoot = true
      LocalStore.itemDetailBackBtnToRoot = false
    }
  },

  methods: {
    clearTrafficCop() {
      this.respondersSinceLastView = [] as IItemResponder[]
    },
    async refreshHistory() {
      // Only refetch history. Refetching task details will
      // override any to or cc updates made by the user since those
      // vars have been tightly-coupled to the formdata.
      await this.fetchHistory()
      await this.$nextTick()

      this.clearTrafficCop()
      this.scrollToLatestUpdates()

      // Restart polling that was halted with traffic cop check...
      this.startPolling()
    },
    async refreshStaleData() {
      await this.fetchData()
      this.scrollToLatestUpdates()
    },
    scrollToLatestUpdates() {
      const newestHistory = this.history.find(h => !h.hasViewed)

      // Perhaps user clicked on an exposed "stale-item-alert" which then
      // loaded the new updates, etc.
      if (newestHistory === undefined) {
        return
      }

      const firstUnviewedEventID = this.history
        .find(h => !h.hasViewed)
        .id.toString()

      const element = document.getElementById(`e${firstUnviewedEventID}`)
      const navBarOffset = this.$screen.isMobile(this.$mq) ? 64 : 36

      window.scrollTo({
        top:
          document.documentElement.scrollTop +
          element.getBoundingClientRect().top -
          navBarOffset,
        behavior: 'smooth',
      })
    },
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      this.poller = setInterval(async () => {
        try {
          await this.checkForStaleData()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async checkForStaleData() {
      const lastUpdatedEpochTime = await TasksAPI.get_LastUpdatedEpochTime(
        this.projectID,
        this.taskID,
      )
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        this.showStaleAlert = true
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    handleEditorToggle(opened: boolean) {
      this.addCommentEditorOpened = opened
    },
    handleTaskMoveCompleted(newProjectID: number) {
      this.showMoveTaskModal = false
      RouteAssistant.goToTask(
        this.$router,
        this.$route.params.accountId,
        newProjectID,
        this.taskID,
        this.$permissions,
        this.projectName,
        this.refNumber,
        this.formData.title,
      )
    },
    async fetchData() {
      this.showStaleAlert = false
      // Fetch new data
      await Promise.all([
        this.fetchTaskData(),
        this.fetchOptions(),
        this.fetchHistory(),
      ])
    },
    async fetchOptions() {
      const response = await TasksAPI.get_FilterOptionsWithTaskID(
        this.projectID,
        this.taskID,
      )

      this.statusOptions = response.listStatuses
      this.priorityOptions = response.listPriorities
      this.watchersOptions = response.listWatchers
      this.assigneesOptions = response.listAssignees
      this.descriptionTemplates = response.listDescriptionTemplates
      this.tagsOptions = response.listTags
      this.atMentionUsers = response.listAtMentionUsers
      this.hasGuestUsers =
        this.watchersOptions.some(u => u.isGuest) ||
        this.assigneesOptions.some(u => u.isGuest)
    },

    async fetchHistory() {
      const response = await TasksAPI.get_History(
        this.projectID,
        this.taskID,
        'chron',
      )

      const allAttachments: IAttachment[] = []
      response.forEach(item => {
        allAttachments.push(...item.attachments)
      })
      this.globalAttachments = uniq(allAttachments)
      this.history = response
    },

    async fetchTaskData() {
      const response = await TasksAPI.get_Detail(this.projectID, this.taskID)

      this.lastAccessEpochTime = response.accessEpochTime

      this.refNumber = response.refNumber
      this.creationDate = response.createdOn
      this.projectName = response.project.name
      this.tags = response.listTags
      this.showHarvestOption = response.harvestEnabled
      this.isPinned = response.isPinned
      this.jobAppointees = response.listJobAppointees
      this.assigneeMandates = response.listAssigneeMandates
      this.currentAssignee = response.assignee
      this.currentStatus = response.status

      this.formData.title = response.title
      this.formData.description = response.description
      this.formData.creator = response.creator
      this.formData.assigneeID = response.assignee ? response.assignee.id : null
      this.formData.statusID = response.status.id
      this.formData.priorityID = response.priority.id
      this.formData.listTags = response.listTags
      this.formData.initialAttachments = response.originalAttachments
      this.formData.dueDate = response.dueDate
      this.formData.listWatchers = response.listWatchers.map(item => item.id)

      this.linkedTasks = response.listLinkedTasks
      this.checklists = response.listChecklists
      this.linkedConversations = response.listLinkedConversations
    },

    async refreshLinkedTasks() {
      try {
        this.linkedTasks = await TasksAPI.get_LinkedTasks(
          this.projectID,
          this.taskID,
        )
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async refreshLinkedConversations() {
      try {
        const response = await TasksAPI.get_LinkedConversations(
          this.projectID,
          this.taskID,
        )

        this.linkedConversations = response
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    handleBackButtonClick() {
      if (this.sendBackButtonToRoot) {
        try {
          this.$router.push(LocalStore.lastItemListRouteVisited)
          return
        } catch {
          // Do nothing, just fallback to base case...
        }
      }

      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.goToProject()
      }
    },

    async getRespondersSinceLastViewed(): Promise<IItemResponder[]> {
      this.clearTrafficCop()
      this.isTrafficCopChecking = true
      const responders = await TasksAPI.get_RespondersSinceLastViewed(
        this.projectID,
        this.taskID,
      )
      this.isTrafficCopChecking = false
      return responders
    },

    async submitComment(formData: ICommentOrReplyData) {
      // Temporarily halt polling...
      this.showStaleAlert = false
      this.endPolling()

      // Get traffic cop info & return if exists...
      const responders = await this.getRespondersSinceLastViewed()
      if (responders.length > 0) {
        this.respondersSinceLastView = responders
        // Note: Polling will continue to halt until user explicitly refreshes for responder updates.
        return
      }

      const data = {
        assigneeID: formData.assigneeID,
        isForAllLinkedTasks: false,
        listAtMentionIDs: formData.listAtMentionIDs,
        listFileUploadIDs: formData.listFileUploadIDs,
        message: formData.message,
        priorityID: formData.priorityID,
        statusID: formData.statusID,
        dueDate: formData.dueDate,
        isInternalComment:
          formData.mode === IChronologyItemTypes.INTERNAL_COMMENT,
      }
      try {
        this.isSubmitting = true
        const response = await TasksAPI.post_CommentWithUpdate(
          this.projectID,
          this.taskID,
          data,
        )
        this.isPostSubmitPreRefetchData = true
        this.showSuccess(response.message)
        await this.fetchData()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isPostSubmitPreRefetchData = false
        this.isSubmitting = false
        this.startPolling()
      }
    },

    /**
     * save Comment
     */
    async editComment(commentId: number, formData: ITasksInputEditComment) {
      try {
        const response = await TasksAPI.put_EditComment(
          this.projectID,
          this.taskID,
          commentId,
          formData,
        )
        this.fetchData()
        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    updateTags(newVal: string[]) {
      if (!isEqual(this.tags, newVal)) {
        this.updateField('tags', newVal)
        this.tags = [...newVal]
      }
    },

    async updateField(field: string, value: any) {
      if (this.isDataLoading) {
        return
      }
      try {
        let response
        switch (field) {
          case 'title':
            response = await TasksAPI.put_Title(
              this.projectID,
              this.taskID,
              value,
            )
            break
          case 'description':
            response = await TasksAPI.put_Description(
              this.projectID,
              this.taskID,
              value,
            )
            break
          case 'status':
            response = await TasksAPI.put_Status(
              this.projectID,
              this.taskID,
              value,
            )
            break
          case 'priority':
            response = await TasksAPI.put_PriorityLevel(
              this.projectID,
              this.taskID,
              value,
            )
            break
          case 'assignee':
            response = await TasksAPI.put_Assignee(
              this.projectID,
              this.taskID,
              value,
            )
            break
          case 'due-date':
            response = await TasksAPI.put_DueDate(
              this.projectID,
              this.taskID,
              value,
            )
            break
          case 'watchers':
            response = await TasksAPI.put_Watchers(
              this.projectID,
              this.taskID,
              value,
            )
            break
          case 'tags':
            response = await TasksAPI.put_Tags(
              this.projectID,
              this.taskID,
              value,
            )
            break
        }
        this.fetchData()
        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async updateJobAppointee(jobID: number, appointeeID: number) {
      if (this.isDataLoading) {
        return
      }
      try {
        const data = {
          appointeeID,
          jobID,
        } as ITasksInputUpdateJobAppointee
        const response = await TasksAPI.put_JobAppointee(
          this.projectID,
          this.taskID,
          data,
        )
        this.fetchData()
        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async deleteTask() {
      try {
        const response = await TasksAPI.delete_Delete(
          this.projectID,
          this.taskID,
        )
        this.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.goToProject()
      }
    },

    async togglePin() {
      try {
        let response
        if (this.isPinned) {
          response = await DashboardAPI.delete_DeletePinnedItem(this.taskID)
        } else {
          response = await DashboardAPI.post_AddPinnedItem(this.taskID)
        }
        this.showSuccess(response.message)
        this.isPinned = !this.isPinned
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    goToProject() {
      RouteAssistant.goToProjectLanding(
        this.$router,
        this.$route.params.accountId,
        this.$route.params.id,
        this.$permissions,
        this.projectName,
      )
    },

    showSuccess(message: string) {
      this.$messaging.showSuccess(message)
    },

    goToTask(task: IBreadcrumbItemMenuItem) {
      /* 
        Continue sending item detail back button to root if
        going between prev/next items 
      */
      LocalStore.itemDetailBackBtnToRoot = true

      /* Handles situation where data may not be loaded yet */
      if (task.entityID === 0 || task.itemID === 0) {
        return
      }

      // Note: There should not be a case where we are going from here to a convo, so
      // we assume the next or prev item is a task.
      RouteAssistant.goToTask(
        this.$router,
        this.$route.params.accountId,
        task.entityID,
        task.itemID,
        this.$permissions,
        task.entityName,
        task.refNumber,
        task.title,
        false,
        null,
        this.$route.params.from_all,
        this.$route.params.from_entity_home,
        this.$route.params.from_kanban_board,
      )
    },

    goToListingWithFilter(val: string) {
      if (this.$route.params.from_entity_home) {
        this.goToProjectListingWithFilter(val)
      } else if (this.$route.params.from_kanban_board) {
        this.goToProjectKanbanWithFilter(val)
      } else if (this.$route.params.from_all) {
        this.goToAllTasksWithFilter(val)
      }
    },

    goToProjectListingWithFilter(val: string) {
      if (ItemFilterAssistant.isDefaultIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToProjectListingWithDefaultFilter(
          this.$router,
          this.$route.params.accountId,
          this.$route.params.id,
          this.$permissions,
          filterID,
        )
      } else if (ItemFilterAssistant.isCustomIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToProjectListingWithCustomFilter(
          this.$router,
          this.$route.params.accountId,
          this.$route.params.id,
          this.$permissions,
          filterID,
        )
      }
    },

    goToProjectKanbanWithFilter(val: string) {
      if (ItemFilterAssistant.isDefaultIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToProjectKanbanWithDefaultFilter(
          this.$router,
          this.$route.params.accountId,
          this.$route.params.id,
          this.$permissions,
          filterID,
        )
      } else if (ItemFilterAssistant.isCustomIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToProjectKanbanWithCustomFilter(
          this.$router,
          this.$route.params.accountId,
          this.$route.params.id,
          this.$permissions,
          filterID,
        )
      }
    },

    goToAllTasksWithFilter(val: string) {
      if (ItemFilterAssistant.isDefaultIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToAllTasksWithDefaultFilter(
          this.$router,
          this.$route.params.accountId,
          filterID,
        )
      } else if (ItemFilterAssistant.isCustomIdentifier(val)) {
        const filterID = ItemFilterAssistant.getIDOfFilterIdentifier(val)
        RouteAssistant.goToAllTasksWithCustomFilter(
          this.$router,
          this.$route.params.accountId,
          filterID,
        )
      }
    },

    handleDelete() {
      this.$confirm(
        `<strong>This action can’t be undone.</strong>
        <br>Are you sure you want to delete this task?`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: 'Delete task',
        },
      ).then(() => {
        this.deleteTask()
      })
    },
  },
})
